import { type RefObject, useCallback, useLayoutEffect, useRef } from 'react';

interface UseFocusManagementOptions {
  // Refs
  inputRef: RefObject<HTMLInputElement | null>;
  dropdownRef: RefObject<HTMLDivElement | null>;

  // State
  isOpen: boolean;
  shouldAutoFocusCalendar: boolean;
}

interface FocusTarget {
  element: HTMLElement;
  reason: 'selected-date' | 'today' | 'first-available';
}

/**
 * Hook to centralize focus management for the date picker
 * Handles auto-focus on calendar, input restoration, and keyboard navigation
 */
export function useFocusManagement(options: UseFocusManagementOptions) {
  const { inputRef, dropdownRef, isOpen, shouldAutoFocusCalendar } = options;

  const previousFocusRef = useRef<HTMLElement | null>(null);

  /**
   * Find the best element to focus in the calendar
   * Priority: selected date > today > first available button
   */
  const findCalendarFocusTarget = useCallback(
    (container: HTMLElement, selectedDate?: string, todayDate?: string): FocusTarget | null => {
      let element: HTMLElement | null = null;
      let reason: FocusTarget['reason'] = 'first-available';

      // Try to focus the selected date
      if (selectedDate) {
        element = container.querySelector(`button[data-date="${selectedDate}"]:not(:disabled)`) as HTMLElement;

        if (element) {
          reason = 'selected-date';

          return { element, reason };
        }
      }

      // Try to focus today's date
      if (todayDate) {
        element = container.querySelector(`button[data-date="${todayDate}"]:not(:disabled)`) as HTMLElement;

        if (element) {
          reason = 'today';

          return { element, reason };
        }
      }

      // Fallback: focus first available button
      element = container.querySelector('button:not(:disabled)') as HTMLElement;

      if (element) {
        return { element, reason };
      }

      return null;
    },
    [],
  );

  /**
   * Focus the calendar when dropdown opens
   * Uses useLayoutEffect to avoid flickering
   */
  useLayoutEffect(() => {
    if (!isOpen || !shouldAutoFocusCalendar) {
      return;
    }

    if (!dropdownRef.current) {
      return;
    }

    // Store the element that had focus before opening
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Find the grid container (day view)
    const gridContainer = dropdownRef.current.querySelector('[role="grid"]') as HTMLElement;

    if (gridContainer) {
      const selectedDate = gridContainer.getAttribute('data-selected-date');
      const todayDate = gridContainer.getAttribute('data-today-date');

      const target = findCalendarFocusTarget(gridContainer, selectedDate || undefined, todayDate || undefined);

      if (target) {
        target.element.focus();

        return;
      }
    }

    // Fallback: focus any button in the dropdown
    const firstButton = dropdownRef.current.querySelector('button:not(:disabled)') as HTMLElement;

    if (firstButton) {
      firstButton.focus();
    }
  }, [isOpen, shouldAutoFocusCalendar, dropdownRef, findCalendarFocusTarget]);

  /**
   * Restore focus to input when dropdown is open but shouldAutoFocusCalendar is false
   * This prevents React ARIA from stealing focus
   */
  useLayoutEffect(() => {
    if (!isOpen || shouldAutoFocusCalendar) {
      return;
    }

    if (!inputRef.current) {
      return;
    }

    // Only refocus if focus was stolen (not on input)
    if (document.activeElement !== inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, shouldAutoFocusCalendar, inputRef]);

  /**
   * Restore focus to previous element when dropdown closes
   */
  useLayoutEffect(() => {
    if (isOpen) {
      return;
    }

    // Dropdown just closed, restore focus to input
    if (inputRef.current && previousFocusRef.current) {
      inputRef.current.blur();
      previousFocusRef.current = null;
    }
  }, [isOpen, inputRef]);

  /**
   * Focus the input field
   */
  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  /**
   * Focus the calendar
   */
  const focusCalendar = useCallback(
    (selectedDate?: string, todayDate?: string) => {
      if (!dropdownRef.current) {
        return;
      }

      const target = findCalendarFocusTarget(dropdownRef.current, selectedDate, todayDate);

      if (target) {
        target.element.focus();
      }
    },
    [dropdownRef, findCalendarFocusTarget],
  );

  /**
   * Handle keyboard navigation from input
   */
  const handleInputKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      handlers: {
        onOpen: (shouldFocusCalendar: boolean) => void;
        onClose: () => void;
        onEscape: () => void;
      },
    ) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          handlers.onOpen(true); // Open and focus calendar
          break;

        case ' ':
          // Only if input is empty
          if ((e.target as HTMLInputElement).value === '' && !isOpen) {
            e.preventDefault();
            handlers.onOpen(true);
          }
          break;

        case 'Escape':
          if (isOpen) {
            e.preventDefault();
            handlers.onClose();
            handlers.onEscape();
          }
          break;

        case 'Enter':
          if (isOpen) {
            e.preventDefault();
            handlers.onClose();
          }
          break;

        default:
          break;
      }
    },
    [isOpen],
  );

  /**
   * Handle Tab key from last navigation button to calendar
   */
  const handleLastNavigationButtonTab = useCallback(
    (e: React.KeyboardEvent, view: 'day' | 'month' | 'year') => {
      if (e.key !== 'Tab' || e.shiftKey) {
        return;
      }

      e.preventDefault();

      if (!dropdownRef.current) {
        return;
      }

      let selector: string;

      switch (view) {
        case 'day':
          selector = 'button[data-date]:not(:disabled)';
          break;
        case 'month':
          selector = 'button[data-month]:not(:disabled)';
          break;
        case 'year':
          selector = 'button[data-year]:not(:disabled)';
          break;
      }

      const firstFocusable = dropdownRef.current.querySelector(selector) as HTMLElement;

      if (firstFocusable) {
        firstFocusable.focus();
      }
    },
    [dropdownRef],
  );

  return {
    // Methods
    focusInput,
    focusCalendar,
    handleInputKeyDown,
    handleLastNavigationButtonTab,
    findCalendarFocusTarget,
  };
}
